import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { formatFull } from "@/lib/format";
import { StatusBadge, ConfBadge } from "@/components/Badges";
import ConsultationList from "@/components/ConsultationList";
import { ArrowLeft, MapPin, Maximize, FileText, Download, ImageOff, FileSignature, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import NdaForm from "@/components/NdaForm";
import DocumentManager from "@/components/DocumentManager";

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 py-3 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className={`font-semibold ${accent ? "text-[#C6A87C]" : "text-[#0F172A]"}`}>{value}</span>
    </div>
  );
}

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [o, setO] = useState(null);
  const [active, setActive] = useState(0);
  const [ndaOpen, setNdaOpen] = useState(false);
  const [ndas, setNdas] = useState([]);

  useEffect(() => {
    api.get(`/opportunities/${id}`).then((r) => {
      setO(r.data);
      setActive(0);
    });
    api.get("/ndas").then((r) => setNdas(r.data.filter((n) => n.opportunity_id === id))).catch(() => {});
  }, [id]);

  if (!o) return <div className="p-12 text-slate-400">Chargement…</div>;

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-12" data-testid="opportunity-detail-page">
      <button
        onClick={() => navigate("/opportunites")}
        data-testid="back-btn"
        className="mb-6 flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-[#0F172A]"
      >
        <ArrowLeft className="h-4 w-4" /> Retour aux opportunités
      </button>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Gallery */}
        <div className="lg:col-span-8">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            {o.photos?.length ? (
              <img src={fileUrl(o.photos[active])} alt={o.title} className="h-[420px] w-full object-cover" />
            ) : (
              <div className="flex h-[420px] w-full flex-col items-center justify-center gap-2 text-slate-300" data-testid="detail-nophoto">
                <ImageOff className="h-10 w-10" />
                <span className="text-sm">Aucune photo pour ce bien</span>
              </div>
            )}
          </div>
          <div className="mt-3 flex gap-3">
            {(o.photos || []).map((p, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                data-testid={`thumb-${i}`}
                className={`h-20 w-28 overflow-hidden rounded-lg border-2 transition-colors ${
                  active === i ? "border-[#C6A87C]" : "border-transparent opacity-70 hover:opacity-100"
                }`}
              >
                <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>

          <div className="mt-8">
            <h2 className="font-display text-2xl text-[#0F172A]">Description</h2>
            <p className="mt-2 leading-relaxed text-slate-600">{o.description}</p>
          </div>

          <div className="mt-8">
            <h2 className="font-display text-2xl text-[#0F172A]">Documents</h2>
            <div className="mt-3">
              <DocumentManager opportunityId={id} />
            </div>
          </div>

          <div className="mt-8">
            <h2 className="font-display text-2xl text-[#0F172A]">Suivi des consultations</h2>
            <p className="mb-3 mt-1 flex items-center gap-1.5 text-sm text-slate-500">
              <Eye className="h-4 w-4 text-[#C6A87C]" /> Ouvertures des teasers envoyés pour ce bien.
            </p>
            <ConsultationList ndas={ndas} emptyText="Aucun teaser envoyé pour cette opportunité." />
          </div>
        </div>

        {/* Side panel */}
        <div className="lg:col-span-4">
          <div className="sticky top-6 rounded-2xl border border-slate-200 bg-white p-7">
            <div className="flex flex-wrap items-center gap-2">
              <ConfBadge level={o.confidentiality} testid="detail-conf" />
              <StatusBadge status={o.status} testid="detail-status" />
            </div>
            <p className="mt-4 text-xs font-medium text-[#C6A87C]">{o.property_type}</p>
            <h1 className="mt-1 font-display text-3xl leading-tight tracking-tight text-[#0F172A]">{o.title}</h1>
            <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-500">
              <MapPin className="h-4 w-4" /> {o.city}, {o.country}
            </p>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
              <Maximize className="h-4 w-4" /> {o.surface} m²
            </p>

            <div className="mt-6 rounded-xl bg-[#0F172A] p-5 text-white">
              <p className="overline text-[#C6A87C]">Marge estimée</p>
              {o.margin == null ? (
                <p className="mt-1 text-sm text-slate-300" data-testid="detail-margin">
                  Marge indisponible – Renseignez la valeur de revente
                </p>
              ) : (
                <>
                  <p className="font-display text-4xl tracking-tight" data-testid="detail-margin">
                    {formatFull(o.margin, o.currency)}
                  </p>
                  <p className="mt-1 text-sm text-slate-400">Rentabilité de {o.margin_pct}%</p>
                </>
              )}
            </div>

            <div className="mt-4">
              <Row label="Prix demandé" value={formatFull(o.asking_price, o.currency)} />
              <Row label="Coût des travaux" value={formatFull(o.renovation_cost, o.currency)} />
              <Row label="Valeur de revente" value={formatFull(o.resale_value, o.currency)} accent />
              <Row label="Devise" value={o.currency} />
            </div>

            <Button
              onClick={() => setNdaOpen(true)}
              data-testid="send-nda-btn"
              className="mt-5 w-full rounded-full bg-[#C6A87C] text-[#0F172A] hover:bg-[#b8986a]"
            >
              <FileSignature className="mr-2 h-4 w-4" /> Envoyer un NDA
            </Button>
          </div>
        </div>
      </div>

      <NdaForm
        open={ndaOpen}
        onOpenChange={setNdaOpen}
        presetOpportunityId={o.id}
        onSaved={() => navigate("/nda")}
      />
    </div>
  );
}
